import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ColumnPipe } from '@sharedModule/pipe/column/column.pipe';
import { Subject, takeUntil } from 'rxjs';
import { TransactionSettingsService } from '../../services/transaction-settings.service';
import { MasterTransactionSettingsInterface } from '../../types/transaction-settings';
import {
  UpsertMasterTransactionSettingsPopupInterface,
  UpsertMasterTransactionSettingsInterface,
} from '../../types/upsert-transaction-settings';

@Component({
  selector: 'app-upsert-master-transaction-settings',
  standalone: true,
  imports: [
    ColumnPipe,
    InputModule,
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
  ],
  templateUrl: './upsert-master-transaction-settings.component.html',
  styleUrl: './upsert-master-transaction-settings.component.scss',
})
export class UpsertMasterTransactionSettingsComponent
  implements OnInit, OnDestroy
{
  public readonly attrConstant = ATTRIBUTES_CONSTANT.settings.transaction;
  upsertTransactionSettingFrom!: FormGroup;
  transactionSettings!: MasterTransactionSettingsInterface[];
  isEdit!: boolean;
  isSubmitLoading!: boolean;
  #destroy$ = new Subject<void>();

  constructor(
    private messageHandlerService: MessageHandlerService,
    @Inject(MAT_DIALOG_DATA)
    public txnData: UpsertMasterTransactionSettingsPopupInterface,
    private transactionSettingsService: TransactionSettingsService,
    public closeDialogRef: MatDialogRef<UpsertMasterTransactionSettingsComponent>
  ) {}

  ngOnInit(): void {
    const isUpdate = this.txnData.isUpdate;
    this.upsertTransactionSettingFrom = new FormGroup({
      [this.attrConstant.name.key]: new FormControl(
        isUpdate ? this.txnData.name : '',
        [Validators.required]
      ),
      [this.attrConstant.value.key]: new FormControl({
        value: isUpdate ? this.txnData.value : '',
        disabled: true,
      }),
      [this.attrConstant.description.key]: new FormControl(
        isUpdate ? this.txnData.description : '',
        [Validators.required]
      ),
    });
  }

  onKeyUp(): void {
    const formValue = this.upsertTransactionSettingFrom.value;
    this.convertToSlug(formValue.name);
  }

  onSubmit(): void {
    if (this.upsertTransactionSettingFrom.valid) {
      this.isSubmitLoading = true;
      const payload = this.upsertTransactionSettingFrom.getRawValue();
      if (this.txnData.isUpdate) this.updateTransactionSetting(payload);
      else this.addTransactionSetting(payload);
    }
  }

  addTransactionSetting(
    payload: UpsertMasterTransactionSettingsInterface
  ): void {
    this.transactionSettingsService
      .addMasterTransactionSetting(this.txnData.selectedTab, payload)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.transactionSettingsService.setDialogData(true);
            this.messageHandlerService.show(x.message);
            this.closeDialogRef.close(true);
          }
        },
        error: error => {
          console.log(error);
          this.closeDialogRef.close(false);
        },
      });
  }

  updateTransactionSetting(
    payload: UpsertMasterTransactionSettingsInterface
  ): void {
    this.transactionSettingsService
      .updateMasterTransactionSetting(this.txnData.selectedTab, {
        ...payload,
        code: this.txnData.code,
      })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.closeDialogRef.close(true);
          }
        },
        error: error => {
          console.log(error);
          this.closeDialogRef.close(false);
        },
      });
  }

  convertToSlug(text: string): void {
    let slug = text.toLowerCase();
    // Replace spaces and slashes with hyphens
    // eslint-disable-next-line no-useless-escape
    slug = slug.replace(/[\s\/]+/g, '-');
    this.upsertTransactionSettingFrom.patchValue({
      value: slug,
    });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
